import * as React from 'react'
import { FormControl, Autocomplete, TextField } from '@mui/material'
import FormHelperText from '@mui/material/FormHelperText'
import { Controller } from 'react-hook-form'

import * as styles from './AutocompleteField.module.scss'
import { ChevronDown } from 'lucide-react'

export const AutocompleteField = ({
  name,
  control,
  label,
  options = [],
  error,
  required,
  disabled,
  defaultValue = '',
  fieldStyle = {},
  hideLabel,
  backgroundColor,
  labelColor,
  placeholder,
  fullWidth = false,
  groupBy,
}) => {
  return (
    <FormControl
      fullWidth={fullWidth}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...(fullWidth ? {} : { maxWidth: '263px', margin: '0 auto' }),
      }}
    >
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={{ required: required ? `${label} is required` : false }}
        render={({ field: { onChange, value } }) => (
          <Autocomplete
            className={styles.autocomplete}
            options={options}
            getOptionLabel={option => option.label || ''}
            value={options.find(opt => opt.value === value) || null}
            onChange={(_, newValue) => {
              onChange(newValue ? newValue.value : '')
            }}
            popupIcon={<ChevronDown />}
            disabled={disabled}
            groupBy={groupBy}
            renderInput={params => (
              <TextField
                {...params}
                label={hideLabel ? '' : label}
                placeholder={placeholder}
                error={!!error}
                required={required}
                className='AutocompleteField'
                sx={{
                  ...fieldStyle,
                  '& .MuiInputBase-root': {
                    backgroundColor: '#4C3371',
                    color: 'white !important',
                    fontWeight: 700,
                    ...(fullWidth
                      ? { width: '100%' }
                      : { maxWidth: '263px', width: '263px' }),
                    ...(backgroundColor && {
                      backgroundColor: backgroundColor,
                    }),
                  },
                  '& .MuiInputBase-input': {
                    color: 'white !important',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4C3371',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4C3371',
                  },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4C3371',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'white !important',
                  },
                  '& .MuiInputLabel-root': {
                    color: labelColor || 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: labelColor || 'white',
                    },
                  },
                  '& .MuiAutocomplete-clearIndicator': {
                    color: 'white !important',
                  },
                  '& .MuiAutocomplete-popupIndicator': {
                    color: 'white !important',
                  },
                }}
              />
            )}
            sx={{
              ...(fullWidth
                ? { width: '100%' }
                : { maxWidth: '263px', width: '263px' }),
            }}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: 'white',
                  maxWidth: fullWidth ? '100%' : '263px',
                  '& .MuiAutocomplete-listbox': {
                    '& .MuiAutocomplete-option': {
                      fontSize: '1rem',
                      padding: '0.75rem 1rem',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                      color: '#000',
                      backgroundColor: 'white',
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        color: '#000',
                      },
                      '&[aria-selected="true"]': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                        color: '#000',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.12)',
                          color: '#000',
                        },
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    },
                  },
                },
              },
            }}
          />
        )}
      />
      {error && <FormHelperText error>{error.message}</FormHelperText>}
    </FormControl>
  )
}
